import React from "react";
import dayjs from "dayjs";
import {
  Card,
  Text,
  Group,
  Badge,
  Menu,
  Button,
  Box,
  Progress,
  Grid,
  Accordion,
  Stack,
} from "@mantine/core";
import {
  IconDotsVertical,
  IconUsers,
  IconClock,
  IconPlayerPlay,
  IconAdjustments,
  IconCircleCheck,
} from "@tabler/icons-react";
import { Project as APIProject } from "api/client/getClientProfile";
import { useNavigate, useParams } from "react-router-dom";
import { UserPlus } from "tabler-icons-react";
import { useUser } from "hooks/useUser";
import { useProject } from "api/projectDetail/getProjectDetail";

interface Task {
  _id: string;
  title: string;
  status: "to do" | "in progress" | "in review" | "done";
  dueDate?: string;
  completedAt?: string;
}

const BUCKET_STATUS_MAP: Record<
  keyof APIProject["tasks"],
  keyof typeof STATUS_CONFIG
> = {
  todo: "to do",
  inProgress: "in progress",
  inReview: "in review",
  completed: "done",
};

const STATUS_CONFIG = {
  "to do": {
    label: "To Do",
    icon: IconClock,
    color: "#eee",
  },
  "in progress": {
    label: "In Progress",
    icon: IconPlayerPlay,
    color: "#e7f5ff",
  },
  "in review": {
    label: "In Review",
    icon: IconAdjustments,
    color: "#fff9db",
  },
  done: {
    label: "Completed",
    icon: IconCircleCheck,
    color: "#ebfbee",
  },
} as const;

interface TaskForgeCardProps {
  projects: APIProject[];
  onOpenDashboard?: () => void;
  // userId: string;
}

export default function ProjectsCard({
  projects,
  // userId,

  onOpenDashboard,
}: TaskForgeCardProps) {
  const { projectId } = useParams();
  // const setSearchParams = useSearchParams()[1];

  const { user } = useUser();

  // const { data, isLoading, isError, isSuccess } = useProject(
  //   projectId!,
  //   user._id
  // );
  const navigate = useNavigate();
  return (
    <Grid>
      {projects.map((project) => {
        const taskGroups = (
          Object.keys(project.tasks) as Array<keyof APIProject["tasks"]>
        ).map((bucketKey) => {
          const rawTasks = project.tasks[bucketKey] as Task[];
          const statusKey = BUCKET_STATUS_MAP[bucketKey];
          const { label, icon: StatusIcon, color } = STATUS_CONFIG[statusKey];
          return {
            bucketKey,
            label,
            Icon: StatusIcon,
            color,
            tasks: rawTasks.map((t) => ({
              id: t._id,
              title: t.title,
              date: t.completedAt || t.dueDate!,
            })),
          };
        });

        return (
          <Grid.Col xs={12} md={6} lg={6} key={project.id}>
            <Card
              shadow="sm"
              radius="md"
              p="md"
              mb="md"
              sx={{ backgroundColor: "#ffffff" }}
            >
              <Group position="apart" align="start" mb="md">
                <Box>
                  <Text weight={700} size="lg">
                    {project.name}
                  </Text>
                  <Text size="sm" color="dimmed">
                    {project.description}
                  </Text>
                </Box>
                <Group spacing="xs">
                  <Badge variant="outline" color="blue">
                    {project.category}
                  </Badge>
                  <Group spacing={4} align="center">
                    <IconUsers size={16} />
                    <Text size="sm">{project.members.length}</Text>
                  </Group>
                  <Menu shadow="sm" width={250}>
                    <Menu.Target>
                      <Button variant="subtle" compact>
                        <IconDotsVertical size={16} /> Actions
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        icon={<UserPlus size={14} />}
                        onClick={() => {
                          navigate(`/project-detail/${project.id}/${user._id}`);
                        }}
                      >
                        Client Dashboard
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Group>
              </Group>

              <Group position="apart" mb="xs">
                <Text size="sm">Project Progress</Text>
                <Text size="sm">{project.taskStats.completionRate}%</Text>
              </Group>
              <Progress
                value={project.taskStats.completionRate}
                size="sm"
                radius="xl"
                mb="md"
              />

              <Accordion variant="separated">
                {taskGroups.map(({ bucketKey, label, Icon, color, tasks }) => {
                  const content = (
                    <Stack spacing="sm">
                      {tasks.map(({ id, title, date }) => (
                        <Card
                          key={id}
                          withBorder
                          radius="sm"
                          p="sm"
                          sx={{
                            backgroundColor: color,
                            borderLeft: "4px solid #ff4d4f",
                          }}
                          onClick={() =>
                            navigate(
                              `/projects/${project.id}?selectedIssue=${id}`
                            )
                          }
                        >
                          <Group position="apart" align="center">
                            <Box>
                              <Text weight={500}>{title}</Text>
                              <Group spacing="xs" align="center" mt={4}>
                                <Icon size={12} />
                                <Text size="xs" color="dimmed">
                                  {label}
                                </Text>
                              </Group>
                            </Box>
                            <Text size="xs" color="dimmed">
                              {bucketKey === "completed"
                                ? `Completed: ${dayjs(date).format(
                                    "MMM D, YYYY"
                                  )}`
                                : `Due: ${dayjs(date).format("MMM D, YYYY")}`}
                            </Text>
                          </Group>
                        </Card>
                      ))}
                    </Stack>
                  );

                  return (
                    <Accordion.Item key={bucketKey} value={bucketKey}>
                      <Accordion.Control icon={<Icon size={16} />}>
                        <Text size="sm" weight={500}>
                          {label} ({tasks.length})
                        </Text>
                      </Accordion.Control>
                      <Accordion.Panel>{content}</Accordion.Panel>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            </Card>
          </Grid.Col>
        );
      })}
    </Grid>
  );
}
